import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { extractErrorMessage } from "../../api/client";
import { usePolling } from "../../hooks/usePolling";
import {
  isLiveEnv,
  listSearchEnvs,
  SearchEnv,
  SEARCH_ENV_STATUS_COLOR,
  SearchEnvStatus,
} from "../../api/searchEnvs";
import { WorkflowDetail } from "../../api/workflows";
import { SearchEnvCard } from "../searchEnv/SearchEnvCard";
import { SearchEnvProvisionModal } from "./SearchEnvProvisionModal";

// Three rendered states:
//   - workflow not eligible (not an export the operator supports,
//     or not yet succeeded): render nothing.
//   - no active env: "Open for analysis" button (+ history of terminal
//     envs if any).
//   - active env (provisioning / indexing / ready): live status card
//     delegated to SearchEnvCard (shared with the /search-envs/<id>
//     detail page so the surfaces stay in lockstep).
//
// Polls /search-envs?workflow_id=X every 10s for the "which env is
// active for this workflow" view; SearchEnvCard handles the per-env
// detail polling at 5s.

interface Props {
  wf: WorkflowDetail;
}

const EXPORT_KINDS = new Set(["batch_export", "month_export", "year_export"]);

// Output formats the operator's search-env reconciler can index. Must
// stay in sync with the gate in src/paim/operator/routes/_search_envs.py
// (api_provision_search_env + sizing-preview). condensed-json yields
// one doc per program (superdoc); ndjson yields one doc per page.
// The operator normalises both shapes into the unified OpenSearch
// index template, so once the env is up they query identically.
const SEARCH_ENV_ELIGIBLE_FORMATS = new Set(["condensed-json", "ndjson"]);

function isEligible(wf: WorkflowDetail): boolean {
  if (!EXPORT_KINDS.has(wf.kind)) return false;
  if (wf.status !== "succeeded") return false;
  try {
    const params = JSON.parse(wf.params_json || "{}");
    return SEARCH_ENV_ELIGIBLE_FORMATS.has(params.output_format ?? "");
  } catch {
    return false;
  }
}

function fmtUsd(n: number | null | undefined): string {
  if (n === undefined || n === null) return "—";
  return `$${n.toFixed(2)}`;
}

export function SearchEnvPanel({ wf }: Props) {
  const eligible = isEligible(wf);
  const [envs, setEnvs] = useState<SearchEnv[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  usePolling({
    fetcher: () => listSearchEnvs({ workflow_id: wf.id }),
    onData: (r) => setEnvs(r.search_envs),
    onError: (err) => setError(extractErrorMessage(err)),
    intervalMs: 10_000,
    deps: [wf.id],
  });

  // "Active" = still has AWS resources (everything except `deleted`).
  // A ``failed`` env stays here too: the OpenSearch domain is still
  // up, costs are still accruing, and the user needs to see it so
  // they can re-index or delete it. Only fully-torn-down envs go
  // to the past-history list.
  const active = envs?.find(isLiveEnv);
  const terminal = envs?.filter((e) => !isLiveEnv(e));

  const reload = () =>
    listSearchEnvs({ workflow_id: wf.id }).then((r) => setEnvs(r.search_envs));

  if (!eligible) return null;
  if (envs === null && !error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <CircularProgress size={18} />
      </Paper>
    );
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="overline" color="text.secondary">
            Search environment
          </Typography>
          {active && (
            <MuiLink
              component={RouterLink}
              to={`/search-envs/${active.id}`}
              variant="caption"
            >
              open detail page →
            </MuiLink>
          )}
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          {active ? (
            <SearchEnvCard envId={active.id} onChanged={reload} />
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
              >
                Open for analysis
              </Button>
              <Typography variant="body2" color="text.secondary">
                Provisions a temporary OpenSearch cluster you can query from
                your browser. TTL-reaped automatically.
              </Typography>
            </Stack>
          )}
        </Box>
        {terminal && terminal.length > 0 && (
          <TerminalEnvHistory envs={terminal} />
        )}
      </Paper>

      <SearchEnvProvisionModal
        open={modalOpen}
        workflowId={wf.id}
        onClose={() => setModalOpen(false)}
        onProvisioned={reload}
      />
    </>
  );
}

function TerminalEnvHistory({ envs }: { envs: SearchEnv[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 2 }}>
      <MuiLink
        component="button"
        type="button"
        variant="caption"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide" : "Show"} {envs.length} past env
        {envs.length === 1 ? "" : "s"}
      </MuiLink>
      <Collapse in={open}>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {envs.map((e) => (
            <Stack
              key={e.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ fontSize: "0.875rem" }}
            >
              <Chip
                label={e.status}
                color={SEARCH_ENV_STATUS_COLOR[e.status as SearchEnvStatus]}
                size="small"
                variant="outlined"
              />
              <MuiLink
                component={RouterLink}
                to={`/search-envs/${e.id}`}
                variant="caption"
                sx={{ fontFamily: "monospace" }}
              >
                {e.id}
              </MuiLink>
              <Typography variant="caption" color="text.secondary">
                {new Date(e.submitted_at).toLocaleString()}
              </Typography>
              {e.incurred_usd !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  · cost {fmtUsd(e.incurred_usd)}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
